import { BaseService } from '../base';
import type {
  ListAuditActivitiesParams,
  ListAuditActivitiesResponse,
} from './types';

export type {
  AuditAction,
  AuditActivityChange,
  AuditActivityItem,
  AuditSubjectType,
  ListAuditActivitiesParams,
  ListAuditActivitiesResponse,
} from './types';

/** Monta a query string ignorando filtro vazio — a API valida `@IsEnum`. */
function buildQuery(params: ListAuditActivitiesParams): string {
  const query = new URLSearchParams();

  if (params.subjectType) query.append('subjectType', params.subjectType);
  if (params.subjectId) query.append('subjectId', params.subjectId);
  if (params.day) query.append('day', params.day);
  if (params.actorSub) query.append('actorSub', params.actorSub);
  if (params.action) query.append('action', params.action);
  if (params.limit) query.append('limit', String(params.limit));
  if (params.cursor) query.append('cursor', params.cursor);

  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Trilha de auditoria transversal — exclusiva do grupo `insurer.administrator`
 * (gate de GRUPO no BFF, não de permissão).
 */
export class AuditService extends BaseService {
  private readonly customBaseUrl: string;

  constructor() {
    super();
    this.customBaseUrl = import.meta.env.VITE_API_URL;
  }

  async listActivities(params: ListAuditActivitiesParams): Promise<{
    success: boolean;
    data?: ListAuditActivitiesResponse;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/audit/activities${buildQuery(params)}`,
        {},
        this.customBaseUrl
      );

      return await this.handleJsonResponse<ListAuditActivitiesResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar a trilha de auditoria',
      };
    }
  }

  /**
   * Export em CSV. Devolve o `Response` CRU de propósito: o chamador precisa do
   * blob e dos headers (`Content-Disposition` para o nome do arquivo,
   * `X-Audit-Truncated` para avisar que o arquivo bateu no teto de linhas) —
   * coisas que o envelope JSON do `handleJsonResponse` descartaria.
   */
  async exportActivities(params: ListAuditActivitiesParams): Promise<Response> {
    // `limit`/`cursor` ficam de fora de propósito: o BFF percorre todas as
    // páginas do filtro até o teto de 10.000 linhas. Mandá-los recortaria o
    // arquivo para a página que está na tela.
    const filters: ListAuditActivitiesParams = {
      subjectType: params.subjectType,
      subjectId: params.subjectId,
      day: params.day,
      actorSub: params.actorSub,
      action: params.action,
    };

    return this.get(
      `/portal/v1/audit/activities/export${buildQuery(filters)}`,
      {},
      this.customBaseUrl
    );
  }
}

export const auditService = new AuditService();
